import FloatingAddLog from '../components/FloatingAddLog';
import LogForm from '../components/LogForm';
import { CustomWaterLoading, MenuPopoverItem } from 'src/components';
import { useAuthentication } from 'src/features/authentication/context';
import { useEffect, useRef, useState, useCallback } from 'react';
import { ApiState, FilterItem, LogItem, SelectedTabType } from '../types';
import { Box, Container, TablePagination } from '@mui/material';
import LogsList from '../components/LogsList';
import LogFilters from '../components/LogFilters';
import useQueryParamsActions from 'src/hooks/useQueryParamsActions';
import parseLogPayload from '../utils/parseLogPayload';
import { useSnackbar } from 'notistack';
import RestoreLogConfirmDialog from '../components/RestoreLogConfirmDialog';
import getAttachments from '../utils/getAttachments';
import { useGetAllSiteAlarms } from 'src/hooks/apiHooks/useGetAllAlarms';
import { AlarmsList } from '../components/alarmsList/AlarmsList';
import { usePagination, useSelectedTab } from '../hooks';
import { useGetAlarmTotalCountForSite } from 'src/hooks/apiHooks/useGetAlarmTotalCountForSite';
import { useResponsive } from 'src/hooks/useResponsive';
import { SitesService } from 'src/services/sites/sites.service';
import { AssetService } from 'src/services/assets/asset.service';
import { LogsService } from 'src/services/logs/logs.service';
import { Asset } from 'src/features/asset-details/types/asset';

const Logs = () => {
  const {
    customerId: { value: customerId },
    siteId: { value: siteId },
  } = useAuthentication();

  const isFirstRender = useRef(true);

  const filtersRef = useRef<any>('');
  const [filters, setFilters] = useState('');
  const [logs, setLogs] = useState<{
    loaded: boolean;
    data: { totalCount: number; results: LogItem[] };
    fetched: boolean;
  }>({
    loaded: false,
    fetched: false,
    data: {
      results: [],
      totalCount: 0,
    },
  });
  const {
    logsPagesRef,
    logsRowsPerPageRef,
    alarmsPagesRef,
    alarmsRowsPerPageRef,
    logsPage,
    logsRowsPerPage,
    onChangeLogsRowsPerPage,
    setLogsPage,
    alarmsPage,
    alarmsRowsPerPage,
    onChangeAlarmsRowsPerPage,
    setAlarmsPage,
  } = usePagination();

  const isMobile = useResponsive('down', 'md');
  const [customerAssets, setCustomerAssets] = useState<Asset[]>([]);
  const { append, get, remove } = useQueryParamsActions();
  const [isCreatingLog, setIsCreatingLog] = useState(false);
  const [isDeletingLog, setIsDeletingLog] = useState(false);
  const [isRevertingLog, setIsRevertingLog] = useState(false);

  const { alarms, isLoaded, getAllSiteAlarms, onAcknowledged, setAlarmData } = useGetAllSiteAlarms({
    siteId: siteId!,
    filters: filters,
    rowsPerPage: alarmsRowsPerPage,
  });
  const { alarmCount, isLoaded: isAlarmCountLoaded } = useGetAlarmTotalCountForSite({
    customerId: customerId!,
    filters: [filters, `siteIdList=${siteId}`].filter(Boolean).join('&'),
  });

  const { selectedTab, onSelectTabHandler } = useSelectedTab();
  const isAlarmSelected = selectedTab === 'Alarms';
  const isLogsSelected = selectedTab === 'Logs';

  const [logToRevert, setLogToRevert] = useState<null | LogItem>(null);

  const [formsState, setFormsState] = useState<
    ApiState<
      {
        name: string;
        id: string;
        allowComments: boolean;
        isDefault: boolean;
      }[]
    >
  >({
    loading: true,

    data: [],
  });
  const formId = get('formId');
  const logId = get('logId');
  const version = get('version');
  const form = formsState.data.find((form) => form.id === formId);
  const fetchedFormsRef = useRef<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();

  const onClose = useCallback(() => {
    remove('formId');
    remove('logId');
    remove('version');
  }, [remove]);

  useEffect(() => {
    if (!customerId || !siteId) return;

    AssetService.getAll(customerId, siteId)

      .then((res) => {
        setCustomerAssets(res);
      })

      .catch((error) => {
        console.error(error);
      });
  }, [customerId, siteId]);

  const fetchLogs = useCallback(
    async (filters = '', lastLogId?: string, limit?: number) => {
      try {
        if (!customerId || !siteId) throw Error('Customer or site is not provided.');
        setLogs((prevState) => ({
          ...prevState,
          loaded: false,
          fetched: true,
        }));
        const logs = await LogsService.getAll(customerId!, siteId!, filters, lastLogId, limit);

        setLogs({
          data: {
            results: logs.results,
            totalCount: logs.totalCount,
          },
          loaded: true,
          fetched: true,
        });
      } catch (err) {
        setLogs({ data: { results: [], totalCount: 0 }, loaded: true, fetched: true });
      }
    },
    [setLogs, customerId, siteId]
  );

  const onEventTabChange = (eventType: SelectedTabType) => {
    onSelectTabHandler(eventType);

    setFilters('');
  };

  // The onFiltersChange doesn't capture change if the chips are being removed.
  useEffect(() => {
    logsPagesRef.current = {};
    alarmsPagesRef.current = {};
    setLogsPage(0);
    setAlarmsPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const onFiltersChange = useCallback(
    async (filter: FilterItem[]) => {
      const filters: string[] = [];

      logsPagesRef.current = {};
      alarmsPagesRef.current = {};
      setLogsPage(0);

      // What is this for?
      const eventTypeList: string[] = [];

      const statusList: string[] = [];
      const priorityList: string[] = [];
      const alarmAcknowledgement: string[] = [];

      filter.forEach((filt) => {
        if (filt.name === 'deleted_logs') {
          filters.push('includeDeletedLogs=true');
        }
        if (filt.name === 'time_frame') {
          filters.push('startDate=' + (filt!.value as string[])[0]);
          filters.push('endDate=' + (filt!.value as string[])[1]);
        }
        if (filt.group.name === 'alarm_type') {
          priorityList.push(filt.name);
        }
        if (filt.group.name === 'alarm_acknowledgement') {
          alarmAcknowledgement.push(filt.name);
        }
        if (filt.group.name === 'event_type' && filt.value === true) {
          eventTypeList.push(filt.name);
        }
        if (filt.group.name === 'alarm_status' && filt.value === true) {
          statusList.push(filt.name);
        }

        if (filt.name === 'assets' && Array.isArray(filt.value)) {
          filt.value.forEach((et: any) => filters.push('assetIdList=' + et.id));
        }
      });

      if (eventTypeList.length) {
        eventTypeList.forEach((et) => filters.push(`eventTypeList=${et}`));
      }
      if (alarmAcknowledgement.length === 1) {
        if (alarmAcknowledgement.includes('acknowledged')) {
          alarmAcknowledgement.forEach((et) => filters.push(`isAcknowledged=true`));
        } else {
          alarmAcknowledgement.forEach((et) => filters.push(`isAcknowledged=false`));
        }
      }

      if (priorityList.length) {
        priorityList.forEach((et) => filters.push(`priorityList=${et}`));
      }
      if (statusList.length) {
        statusList.forEach((et) => filters.push(`statusList=${et}`));
      }

      setFilters(filters.join('&'));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setLogsPage]
  );

  // Log operations
  const handleCreateLog = async (data: any, logFormId?: string, allowComments?: boolean) => {
    if (!form?.id && !logFormId) return;

    const log: any = parseLogPayload(
      data,
      (form?.id || logFormId) as string,
      !!allowComments,
      logId
    );

    const attachments = getAttachments(data);

    if (!customerId || !siteId) return;

    const maxSizeInMB = 10; // 10 MB
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024; // Convert MB to Bytes

    let hasTooLongFile = false;
    Object.values(attachments).forEach((attachment: any) => {
      if (Array.isArray(attachment)) {
        attachment.forEach((file: any) => {
          if (file?.size > maxSizeInBytes) {
            hasTooLongFile = true;
          }
        });
      }
    });

    if (hasTooLongFile)
      return enqueueSnackbar('Uploaded file size is too long! (Maximum is: 10mb)', {
        variant: 'error',
      });

    try {
      setIsCreatingLog(true);
      await LogsService.add(customerId, siteId, log, attachments, logId ?? undefined);
      await fetchLogs(filtersRef.current);
      enqueueSnackbar('Log successfully created!', { variant: 'success' });
      setIsCreatingLog(false);
      onClose();
    } catch (err) {
      enqueueSnackbar('Log creation failed!', { variant: 'error' });
      setIsCreatingLog(false);
    }
  };
  const handleDeleteLog = async (logId: string) => {
    try {
      setIsDeletingLog(true);
      await LogsService.patch(customerId!, siteId!, logId, { status: 'inactive' });

      enqueueSnackbar('Log successfully deleted!', { variant: 'success' });
      setIsDeletingLog(false);
      onClose();
      await fetchLogs(filtersRef.current);
    } catch (err) {
      enqueueSnackbar('Log deletion failed! Message', { variant: 'error' });
      setIsDeletingLog(false);
    }
  };
  const handleRevertLog = async (logId: string) => {
    try {
      setIsRevertingLog(true);
      await LogsService.patch(customerId!, siteId!, logId, { status: 'active' });

      enqueueSnackbar('Log successfully reverted!', { variant: 'success' });
      setIsRevertingLog(false);
      setLogToRevert(null);
      await fetchLogs(filtersRef.current);
    } catch (err) {
      enqueueSnackbar('Log revert failed! Message', { variant: 'error' });
      setIsRevertingLog(false);
      setLogToRevert(null);
    }
  };

  useEffect(() => {
    filtersRef.current = filters;

    fetchLogs(filters, undefined, logsRowsPerPageRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchLogs, filters]);

  useEffect(() => {
    if (!fetchedFormsRef.current) {
      (async () => {
        const data = await SitesService.getAssignedForms(customerId!, siteId!);
        setFormsState({ loading: false, data });
        fetchedFormsRef.current = true;
      })();
    }
  }, [customerId, siteId]);

  const logsPageRef = useRef(logsPage);
  const alarmsPageRef = useRef(alarmsPage);

  useEffect(() => {
    logsPageRef.current = logsPage;
    alarmsPageRef.current = alarmsPage;
  }, [logsPage, alarmsPage]);

  const logsRef = useRef<any>([]);
  const alarmsRef = useRef<any>([]);

  useEffect(() => {
    logsRef.current = logs.data.results;
    alarmsRef.current = alarms;
  }, [logs, alarms]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    setLogsPage(0);
    setAlarmsPage(0);

    logsPagesRef.current = {};
    alarmsPagesRef.current = {};

    if (selectedTab === 'Logs') {
      fetchLogs(filtersRef.current, undefined, logsRowsPerPageRef.current);
    } else if (selectedTab === 'Alarms') {
      getAllSiteAlarms({
        limit: alarmsRowsPerPageRef.current,
        siteId: siteId!,
        filters: filtersRef.current,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  const formsNotAvailable = !formsState.loading && (!formsState.data || !formsState.data.length);

  return (
    <Container
      maxWidth={false}
      sx={{
        paddingInline: '0px !important',
        display: 'flex',
        height: isMobile ? '100%' : 'calc(100% - 138px)',
        flexDirection: 'column',
      }}
    >
      <LogFilters
        onChange={onFiltersChange}
        selectedTab={selectedTab}
        onSelectTabHandler={onEventTabChange}
      />

      {((isLogsSelected && logs.loaded) || (isAlarmSelected && isLoaded && isAlarmCountLoaded)) && (
        <TablePagination
          component={'div'}
          sx={{
            borderTop: 0,
            height: '42px',
            overflow: 'hidden',
            '.MuiTablePagination-selectLabel': { mt: 0, mb: 0 },
            '.MuiTablePagination-displayedRows': { mt: 0, mb: 0 },
            '.MuiTablePagination-actions': { ml: 0 },
          }}
          count={isAlarmSelected ? alarmCount?.total : logs.data.totalCount}
          page={isAlarmSelected ? alarmsPage : logsPage}
          rowsPerPage={isAlarmSelected ? alarmsRowsPerPage : logsRowsPerPage}
          onPageChange={(event: unknown, nextPage) => {
            let skipTo = '';
            // Because the filter is for a specific site the cursor for site id can be the current site ID.
            let skipToSiteId = siteId || '';
            let isBack = null;

            if (isAlarmSelected) {
              isBack = nextPage < alarmsPageRef.current;
            } else {
              isBack = nextPage < logsPageRef.current;
            }

            if (nextPage && !isBack && isLogsSelected) {
              logsPagesRef.current[nextPage] = logsRef.current[logsRef.current.length - 1]?.id;
            }
            if (nextPage && !isBack && isAlarmSelected) {
              alarmsPagesRef.current[nextPage] =
                alarmsRef.current[alarmsRef.current.length - 1]?.id;
            }
            if (isLogsSelected) {
              if (isBack) {
                skipTo = logsPagesRef.current[nextPage];
              } else {
                const lastLogId = logsRef.current[logsRef.current.length - 1]?.id;
                skipTo = lastLogId || '';
              }
            }
            if (isAlarmSelected) {
              if (isBack) {
                skipTo = alarmsPagesRef.current[nextPage];
                if (nextPage === 0) {
                  skipToSiteId = '';
                }
              } else {
                const lastAlarmId = alarmsRef.current[alarmsRef.current.length - 1]?.id;
                skipTo = lastAlarmId || '';
              }
            }

            if (isAlarmSelected) {
              getAllSiteAlarms({
                limit: alarmsRowsPerPageRef.current,
                skipTo: skipTo,
                skipToSiteId: skipToSiteId,
                siteId: siteId!,
                filters: filters,
              });
              setAlarmsPage(nextPage);
            } else {
              fetchLogs(filtersRef.current, skipTo, logsRowsPerPageRef.current);
              setLogsPage(nextPage);
            }
          }}
          onRowsPerPageChange={(event) => {
            if (isAlarmSelected) {
              setAlarmsPage(0);
              alarmsPagesRef.current = {};
              alarmsRowsPerPageRef.current = parseInt(event.target.value) as number;
              getAllSiteAlarms({
                limit: alarmsRowsPerPageRef.current,
                siteId: siteId!,
                filters: filters,
              });
              onChangeAlarmsRowsPerPage(event as any);
            } else {
              setLogsPage(0);
              logsPagesRef.current = {};
              logsRowsPerPageRef.current = parseInt(event.target.value) as number;
              fetchLogs(filtersRef.current, undefined, parseInt(event.target.value) as number);
              onChangeLogsRowsPerPage(event as any);
            }
          }}
        />
      )}

      <RestoreLogConfirmDialog
        loading={isRevertingLog}
        onConfirm={async () => handleRevertLog(logToRevert!.id)}
        onClose={() => {
          setLogToRevert(null);
        }}
        open={!!logToRevert}
        logTitle={logToRevert?.title || ''}
      />

      {isAlarmSelected && (
        <>
          {!isLoaded || !isAlarmCountLoaded ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
              }}
            >
              <CustomWaterLoading />
            </Box>
          ) : (
            <AlarmsList
              alarms={alarms}
              isLoaded={isLoaded}
              onAcknowledged={onAcknowledged}
              setAlarmData={setAlarmData}
            />
          )}
        </>
      )}

      {isLogsSelected && (
        <>
          {!logs.loaded ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
              }}
            >
              <CustomWaterLoading />
            </Box>
          ) : (
            <LogsList
              onLogRevert={setLogToRevert}
              onLogSelected={(log) => {
                append('logId', log.id);
              }}
              logs={logs.data.results}
            />
          )}
        </>
      )}

      {((isLogsSelected && logs.loaded) || (isAlarmSelected && isLoaded && isAlarmCountLoaded)) && (
        <TablePagination
          component={'div'}
          sx={{
            borderTop: 0,
            marginBottom: '90px',
            height: '42px',
            overflow: 'hidden',
            '.MuiTablePagination-selectLabel': { mt: 0, mb: 0 },
            '.MuiTablePagination-displayedRows': { mt: 0, mb: 0 },
            '.MuiTablePagination-actions': { ml: 0 },
          }}
          count={isAlarmSelected ? alarmCount?.total : logs.data.totalCount}
          page={isAlarmSelected ? alarmsPage : logsPage}
          rowsPerPage={isAlarmSelected ? alarmsRowsPerPage : logsRowsPerPage}
          onPageChange={(event: unknown, nextPage) => {
            let skipTo = '';
            let skipToSiteId = siteId || '';
            let isBack = null;

            if (isAlarmSelected) {
              isBack = nextPage < alarmsPageRef.current;
            } else {
              isBack = nextPage < logsPageRef.current;
            }

            if (nextPage && !isBack && isLogsSelected) {
              logsPagesRef.current[nextPage] = logsRef.current[logsRef.current.length - 1]?.id;
            }
            if (nextPage && !isBack && isAlarmSelected) {
              alarmsPagesRef.current[nextPage] =
                alarmsRef.current[alarmsRef.current.length - 1]?.id;
            }
            if (isLogsSelected) {
              if (isBack) {
                skipTo = logsPagesRef.current[nextPage];
              } else {
                const lastLogId = logsRef.current[logsRef.current.length - 1]?.id;
                skipTo = lastLogId || '';
              }
            }
            if (isAlarmSelected) {
              if (isBack) {
                skipTo = alarmsPagesRef.current[nextPage];
                if (nextPage === 0) {
                  skipToSiteId = '';
                }
              } else {
                const lastAlarmId = alarmsRef.current[alarmsRef.current.length - 1]?.id;
                skipTo = lastAlarmId || '';
              }
            }

            if (isAlarmSelected) {
              getAllSiteAlarms({
                limit: alarmsRowsPerPageRef.current,
                skipTo: skipTo,
                skipToSiteId: skipToSiteId,
                siteId: siteId!,
                filters: filters,
              });
              setAlarmsPage(nextPage);
            } else {
              setLogsPage(nextPage);
              fetchLogs(filtersRef.current, skipTo, logsRowsPerPageRef.current);
            }
          }}
          onRowsPerPageChange={(event) => {
            if (isAlarmSelected) {
              setAlarmsPage(0);
              alarmsPagesRef.current = {};
              alarmsRowsPerPageRef.current = parseInt(event.target.value) as number;
              getAllSiteAlarms({
                limit: alarmsRowsPerPageRef.current,
                siteId: siteId!,
                filters: filters,
              });
              onChangeAlarmsRowsPerPage(event as any);
            } else {
              setLogsPage(0);
              logsPagesRef.current = {};
              logsRowsPerPageRef.current = parseInt(event.target.value) as number;
              fetchLogs(filtersRef.current, undefined, parseInt(event.target.value) as number);
              onChangeLogsRowsPerPage(event as any);
            }
          }}
        />
      )}

      {/* Form modal */}
      {form || logId ? (
        <LogForm
          onChangeVersionHistory={(historyItem, lastItem) => {
            if (lastItem && lastItem.version === historyItem.version) return remove('version');
            append('version', historyItem.version + '');
          }}
          version={version ?? undefined}
          isCreatingLog={isCreatingLog}
          isDeletingLog={isDeletingLog}
          logId={logId}
          onSubmit={handleCreateLog}
          onDelete={handleDeleteLog}
          onClose={onClose}
          isDefault={form?.isDefault}
          id={form?.id}
          customerAssets={customerAssets}
        />
      ) : (
        <></>
      )}
      <FloatingAddLog
        disabled={formsState.loading || formsNotAvailable}
        renderActions={(onClose) => (
          <>
            {formsState.data.map((form) => (
              <MenuPopoverItem
                key={form.id}
                handleClick={() => {
                  onClose();

                  remove('logId');
                  append('formId', form.id);
                }}
                customContent={<>{form.name}</>}
              />
            ))}
          </>
        )}
      />
    </Container>
  );
};

export default Logs;
